/**
 * One kid in one class session: their notes, Story Time, and class scans
 * together. Items with no class session stay in a per-student bucket so they
 * are not a detached section under the whole stream.
 */
import dayjs from "dayjs"
import { sortBy } from "lodash"

import {
  isArtworkDontShare,
  isClassImageShared,
  type ReviewClassImage,
} from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import { type StoryTimeConstructor } from "#customTypes/composedTypes/storyTimeTypes"
import { isStoryTimeShared } from "#utils/storyTimeHelpers"

import {
  familyReviewBucket,
  getMomentLevel,
  sortMomentsForDisplay,
  type StandoutReviewJoins,
} from "./standoutReviewHelpers"

export const NO_CLASS_SESSION_GROUP = "none"

/**
 * The student's current level for this session's curriculum. Moments win,
 * then the class. Missing ladder means no level to show.
 */
export const levelForStudentSession = (
  group: Pick<
    StudentSessionGroup,
    "studentId" | "moments" | "storyTimes" | "classImages"
  >,
  joins: Pick<
    StandoutReviewJoins,
    "studentsById" | "curriculaById" | "classesById"
  >
): ReturnType<typeof getMomentLevel> => {
  const curriculumIds = group.moments
    .map((moment) => moment.curriculumId)
    .filter((id): id is string => !!id)
  const classId =
    group.moments.find((moment) => moment.classId)?.classId ??
    group.storyTimes.find((storyTime) => storyTime.classId)?.classId ??
    group.classImages.find((row) => row.artwork.classId)?.artwork.classId
  const classCurriculumId = classId
    ? joins.classesById[classId]?.curriculumId
    : undefined
  if (classCurriculumId) curriculumIds.push(classCurriculumId)
  for (const curriculumId of curriculumIds) {
    const level = getMomentLevel(
      { studentId: group.studentId, curriculumId },
      joins.studentsById,
      joins.curriculaById
    )
    if (level) return level
  }
  return null
}

export interface StudentSessionGroup {
  key: string
  studentId: string
  classSessionId: string | null
  label: string
  resolved: boolean
  /** Class name and date, or "No class session". */
  subtitle: string
  moments: StandoutMomentConstructor[]
  storyTimes: StoryTimeConstructor[]
  classImages: ReviewClassImage[]
}

export type SessionShareTarget =
  | { kind: "moment"; id: string }
  | { kind: "storyTime"; id: string }
  | { kind: "image"; studentId: string; artworkId: string }

const sessionKey = (studentId: string, classSessionId: string | null): string =>
  `${studentId}::${classSessionId ?? NO_CLASS_SESSION_GROUP}`

export const sessionShareKey = (target: SessionShareTarget): string => {
  switch (target.kind) {
    case "moment":
      return `moment:${target.id}`
    case "storyTime":
      return `story:${target.id}`
    case "image":
      return `image:${target.studentId}:${target.artworkId}`
  }
}

/**
 * A parent-facing sentence: the teacher's note or the generated rewrite.
 * Keywords alone, and a blank or missing string, are not a message.
 */
export const momentHasFamilyText = (moment: {
  note?: string | null
  generatedNote?: string | null
}): boolean =>
  (moment.note?.trim() ?? "") !== "" ||
  (moment.generatedNote?.trim() ?? "") !== ""

/**
 * Items the header checkbox can select and Share will send. Already-shared
 * rows stay on the card with their own checkbox; they are not in this set.
 * A scanner note the teacher unshared is pending again, so it is included.
 * A moment with no class session cannot be delivered, so it is omitted.
 * A pending moment with no message (keywords only) is omitted: there is
 * nothing to score or share until someone writes one. A message or photo
 * marked Don't share is omitted until someone allows share again.
 * A secret is omitted: parents never receive it.
 */
export const unsharedTargets = (
  group: StudentSessionGroup
): SessionShareTarget[] => {
  const targets: SessionShareTarget[] = []
  for (const moment of group.moments) {
    if (
      familyReviewBucket(moment) === "pending" &&
      momentHasFamilyText(moment) &&
      moment.dontShare !== true &&
      moment.category !== "secret"
    ) {
      targets.push({ kind: "moment", id: moment.id })
    }
  }
  for (const storyTime of group.storyTimes) {
    if (storyTime.classSessionId && !isStoryTimeShared(storyTime)) {
      targets.push({ kind: "storyTime", id: storyTime.id })
    }
  }
  for (const row of group.classImages) {
    if (!isClassImageShared(row.artwork) && !isArtworkDontShare(row.artwork)) {
      targets.push({
        kind: "image",
        studentId: row.studentId,
        artworkId: row.artwork.id,
      })
    }
  }
  return targets
}

/**
 * True when the cron would still send this note. Approved counts. A legacy
 * row with `isShared` and no `familyState` counts. Pending, sent, and
 * secrets do not. Review's "auto" bucket is wider: a scanner note left
 * pending is auto there and is not shared here.
 */
const momentKeepsFamilyClock = (
  moment: Pick<
    StandoutMomentConstructor,
    "category" | "familyState" | "isShared" | "dontShare"
  >
): boolean => {
  if (moment.category === "secret" || moment.dontShare === true) return false
  if (moment.familyState === "approved") return true
  return moment.familyState == null && moment.isShared === true
}

const sameShareTarget = (
  excluding: SessionShareTarget,
  kind: SessionShareTarget["kind"],
  id: string,
  studentId?: string
): boolean => {
  if (excluding.kind !== kind) return false
  if (excluding.kind === "image") {
    return excluding.artworkId === id && excluding.studentId === studentId
  }
  return excluding.id === id
}

/**
 * True when some other note, Story Time, or scan for this student and class
 * session is still shared. Secrets do not count. Unsharing the last real
 * item cancels the delivery; unsharing one of several leaves the clock.
 */
export const otherFamilyItemsShared = (
  items: {
    moments: readonly Pick<
      StandoutMomentConstructor,
      | "id"
      | "studentId"
      | "classSessionId"
      | "category"
      | "isShared"
      | "familyState"
      | "dontShare"
      | "source"
    >[]
    storyTimes: readonly Pick<
      StoryTimeConstructor,
      "id" | "studentId" | "classSessionId" | "isShared"
    >[]
    classImages: readonly ReviewClassImage[]
  },
  studentId: string,
  classSessionId: string,
  excluding: SessionShareTarget
): boolean => {
  for (const moment of items.moments) {
    if (moment.studentId !== studentId) continue
    if (moment.classSessionId !== classSessionId) continue
    if (sameShareTarget(excluding, "moment", moment.id)) continue
    // Same rule as the cron. A pending scanner note is "auto" in review
    // (it arrived shared-looking) but it is not approved, so it must not
    // keep the clock after the last real item is unshared.
    if (momentKeepsFamilyClock(moment)) return true
  }
  for (const storyTime of items.storyTimes) {
    if (storyTime.studentId !== studentId) continue
    if (storyTime.classSessionId !== classSessionId) continue
    if (sameShareTarget(excluding, "storyTime", storyTime.id)) continue
    if (isStoryTimeShared(storyTime)) return true
  }
  for (const row of items.classImages) {
    if (row.studentId !== studentId) continue
    if (row.artwork.classSessionId !== classSessionId) continue
    if (row.artwork.isHidden === true) continue
    if (sameShareTarget(excluding, "image", row.artwork.id, row.studentId)) {
      continue
    }
    if (isClassImageShared(row.artwork)) return true
  }
  return false
}

/** "1 item shared", "3 items unshared". */
export const itemsSharedMessage = (
  count: number,
  verb: "shared" | "unshared"
): string => `${count} ${count === 1 ? "item" : "items"} ${verb}`

/**
 * Inside a class panel the class name is already the panel title. The
 * student card keeps the date (or "No class session").
 */
export const sessionSubtitleForNesting = (
  subtitle: string,
  nested: boolean
): string => {
  if (!nested) return subtitle
  const splitAt = subtitle.indexOf(" · ")
  if (splitAt === -1) return subtitle
  return subtitle.slice(splitAt + 3)
}

/**
 * One row in a student's session card, for ordering only. A TA comment
 * carries the TA's name; a teacher comment leaves it off.
 */
export interface SessionFeedSortable {
  kind: "image" | "story" | "moment"
  at: number
  id: string
  taName?: string
}

/** Images, then Story Time, then teacher comments, then TA comments by name. */
export const compareSessionFeedEntries = (
  a: SessionFeedSortable,
  b: SessionFeedSortable
): number => {
  const rank = (entry: SessionFeedSortable): number => {
    if (entry.kind === "image") return 0
    if (entry.kind === "story") return 1
    return entry.taName ? 3 : 2
  }
  const byRank = rank(a) - rank(b)
  if (byRank !== 0) return byRank
  if (a.taName && b.taName) {
    const byName = a.taName.localeCompare(b.taName, undefined, {
      sensitivity: "base",
    })
    if (byName !== 0) return byName
  }
  if (a.at !== b.at) return b.at - a.at
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

const latestTime = (times: number[]): number | null => {
  const usable = times.filter((time) => time > 0)
  if (usable.length === 0) return null
  return Math.max(...usable)
}

const fillSubtitle = (
  group: StudentSessionGroup,
  joins: Pick<StandoutReviewJoins, "classesById">
): void => {
  if (!group.classSessionId) {
    group.subtitle = "No class session"
    return
  }
  const classId =
    group.moments.find((moment) => moment.classId)?.classId ??
    group.storyTimes.find((storyTime) => storyTime.classId)?.classId ??
    group.classImages.find((row) => row.artwork.classId)?.artwork.classId
  const className = classId ? joins.classesById[classId]?.name : undefined
  const when = latestTime([
    ...group.moments.map((moment) => dayjs(moment.createdAt).valueOf()),
    ...group.storyTimes.map((storyTime) => storyTime.timeCreated ?? 0),
    ...group.classImages.map((row) => row.artwork.timeCreated ?? 0),
  ])
  const dateLabel = when ? dayjs(when).format("ddd, MMM D YYYY") : null
  group.subtitle = [className ?? "Class", dateLabel]
    .filter((part): part is string => !!part)
    .join(" · ")
}

export const buildStudentSessionGroups = (
  moments: readonly StandoutMomentConstructor[],
  storyTimes: readonly StoryTimeConstructor[],
  classImages: readonly ReviewClassImage[],
  joins: Pick<StandoutReviewJoins, "studentsById" | "classesById">
): StudentSessionGroup[] => {
  const groups = new Map<string, StudentSessionGroup>()

  const ensure = (
    studentId: string,
    classSessionId: string | null
  ): StudentSessionGroup => {
    const key = sessionKey(studentId, classSessionId)
    const existing = groups.get(key)
    if (existing) return existing
    const student = joins.studentsById[studentId]
    const group: StudentSessionGroup = {
      key,
      studentId,
      classSessionId,
      label: student?.name ?? "Unknown student",
      resolved: !!student,
      subtitle: "",
      moments: [],
      storyTimes: [],
      classImages: [],
    }
    groups.set(key, group)
    return group
  }

  for (const moment of moments) {
    ensure(moment.studentId, moment.classSessionId ?? null).moments.push(moment)
  }
  for (const storyTime of storyTimes) {
    ensure(
      storyTime.studentId,
      storyTime.classSessionId ?? null
    ).storyTimes.push(storyTime)
  }
  for (const row of classImages) {
    ensure(row.studentId, row.artwork.classSessionId ?? null).classImages.push(
      row
    )
  }

  for (const group of groups.values()) {
    group.moments = sortMomentsForDisplay(group.moments)
    group.storyTimes = sortBy(
      group.storyTimes,
      (storyTime) => -(storyTime.timeCreated ?? 0)
    )
    group.classImages = sortBy(
      group.classImages,
      (row) => -(row.artwork.timeCreated ?? 0)
    )
    fillSubtitle(group, joins)
  }

  return sortBy(
    [...groups.values()],
    [
      (group) =>
        -(
          latestTime([
            ...group.moments.map((moment) => dayjs(moment.createdAt).valueOf()),
            ...group.storyTimes.map((storyTime) => storyTime.timeCreated ?? 0),
            ...group.classImages.map((row) => row.artwork.timeCreated ?? 0),
          ]) ?? 0
        ),
      (group) => group.key,
    ]
  )
}
